/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';
import type UserAgentInfo from 'js/lib/useragent';

import type { Theme } from '@coursera/cds-core';
import { breakpoints, typography2 } from '@coursera/cds-core';
import type { ButtonName, SectionName } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import CourseraPlusMonthlyToAnnualUpgradeContainer from 'bundles/coursera-plus-common/components/monthlyToAnnualUpgrade/CourseraPlusMonthlyToAnnualUpgradeContainer';
import type { PropsFromCaller as BannerEnrollTitleComponentProps } from 'bundles/enroll-common/components/xdp/MultipleLearningProgramsLink';
import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import ReactivateSubscriptionXdpCtaButton from 'bundles/subscriptions-common/components/ReactivateSubscriptionXdpCtaButton';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';

import _t from 'i18n!nls/xdp';

export type PropsFromEnrollHocs = {
  buttonTrackingName?: string;
  buttonLink?: string;
  // The contents for the enroll button, can contain multiple elements.
  // Typically it's a span with some 'enroll' text and a div element with a start date.
  buttonLabel?: string;
  // A span element that contains a message relating to current user state.
  title?: string;
  // A component that displays a link about the current user being in multiple learning programs.
  TitleComponent?: React.ComponentType<BannerEnrollTitleComponentProps>;
  // Typically contains the finaid link, can contain more than one element.
  subtitleElem?: React.ReactNode;
  onClick?: () => void;
  // Used with TitleComponent. This will only be defined if TitleComponent is defined.
  onMultipleLearningProgramsLinkClick?: () => void;
  buttonDisabled?: boolean;
  buttonLoading?: boolean;
  userAgent?: UserAgentInfo;
  enrollmentAvailableChoices?: EnrollmentChoices;
  isEnrolled?: boolean;
  isEligibleForPlusMonthlyToAnnualUpgrade: boolean;
  isCourseraPlusEligibleProduct?: boolean;
  showUndoCancellationCTA?: boolean;
  partnerIds?: string[];
  onStickyHeader?: boolean;
  isS12nUpgradeEligible?: boolean;
  s12nUpgradeLink?: string;
};

const styles = {
  enrollButtonContainer: (showAdditionalCTA?: boolean) => css`
    display: flex;
    gap: var(--cds-spacing-100) var(--cds-spacing-200);
    align-items: ${showAdditionalCTA ? 'unset' : 'center'};
    flex-wrap: wrap;

    .dual-cta-subtext {
      font-size: 0.8rem;

      .rc-StartDateString {
        line-height: inherit;
      }
    }

    ${showAdditionalCTA
      ? `
  form {
  display: flex;

   button {
   padding: var(--cds-spacing-150) var(--cds-spacing-300);
   
      ${breakpoints.up('md')} {
       min-width: 275px;
       max-width: 275px;
    }
  }
  }`
      : ''}
  `,
  buttonContainer: css`
    width: 100%;
    display: inline-block;
  `,
  additionalLinksContainer: () => css`
    display: inline-block;
    vertical-align: middle;

    > div:not(:first-child) {
      margin-top: var(--cds-spacing-100);
    }
  `,
  subtitleContainer: (theme: Theme) => css`
    .subtitle-container {
      margin: 0 !important;
      font-size: ${theme.typography.body2.fontSize} !important;
      font-weight: ${theme.typography.body2.fontWeight} !important;
      line-height: ${theme.typography.body2.lineHeight} !important;
      letter-spacing: ${theme.typography.body2.letterSpacing} !important;

      button.button-link,
      > div {
        font-size: 13px !important;
      }

      svg {
        display: inline-block;
        position: relative;
        margin-left: 6px;
        top: 3px;
      }
    }
  `,
  button: css`
    display: flex;
    width: 100%;
    max-width: 100%;
    padding: 11px 32px 13px 32px;

    .rc-StartDateString {
      line-height: 1rem;
    }
  `,
  title: () => css`
    ${typography2.bodySecondary}

    span {
      ${typography2.bodySecondary}
    }
  `,
  biggerButton: css`
    width: auto;
    max-width: 100%;
    padding: 11px 32px 13px 32px;

    ${breakpoints.up('sm')} {
      padding: 11px var(--cds-spacing-1000) 13px;
    }

    .rc-StartDateString {
      line-height: 1rem;
    }
  `,
};

// Prevent default if TTI is achieved so modal will open as normal.
// If button is clicked before hydration/TTI the form will submit to the current URL + ?action=enroll or go to course home if enrolled.
const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
  e.preventDefault();
};

export const EnrollButtonContent = (props: PropsFromEnrollHocs) => {
  const {
    buttonLabel,
    title,
    subtitleElem,
    onClick,
    buttonDisabled,
    buttonLoading,
    isEnrolled,
    isEligibleForPlusMonthlyToAnnualUpgrade,
    isCourseraPlusEligibleProduct,
    showUndoCancellationCTA,
    TitleComponent,
    onStickyHeader,
    onMultipleLearningProgramsLinkClick,
    buttonTrackingName,
    isS12nUpgradeEligible,
    s12nUpgradeLink,
  } = props;
  const { pathname, query } = useLocation();

  const coursePageData = useCoursePageData();
  const s12nPageData = useSpecializationPageData();
  const track = useTracker();
  const eventingProductData = useEventingProductData();

  const pageSectionName: SectionName = onStickyHeader ? 'sticky_page_navigation' : 'hero_banner';

  const enrollButtonTrackingData = {
    button: {
      name: isEnrolled ? ('go_to_course' as const) : ('enroll' as const),
    },
    pageSection: {
      sectionName: pageSectionName,
    },
    ...eventingProductData,
  };

  const viewUpdatesButtonTrackingData = {
    button: {
      name: 'view_updates' as const,
    },
    pageSection: {
      sectionName: pageSectionName,
    },
    ...eventingProductData,
  };

  const enrollButtonRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_button',
    enrollButtonTrackingData
  );

  const handleEnrollButtonClick = () => {
    track('click_button', enrollButtonTrackingData);

    onClick?.();
  };

  const handleViewUpdatesButtonClick = (link: string | undefined) => {
    if (link) {
      track('click_button', viewUpdatesButtonTrackingData);
      window.location.assign(link);
    }
  };

  const handleEnterpriseButtonClick = (link: string, trackingName: ButtonName) => {
    track('click_button', {
      button: {
        name: trackingName,
      },
      ...eventingProductData,
    });

    window.location.assign(link);
  };

  const shouldShowBiggerCTA = pageSectionName === 'hero_banner' && GrowthDiscoveryExperiments.get('showBiggerCTAonXDP');
  const showMonthlyToAnnualUpgradeButton = isEligibleForPlusMonthlyToAnnualUpgrade;
  const productItemId = coursePageData?.primarySpecialization?.id ?? s12nPageData?.id;
  const showAdditionalCTA = showMonthlyToAnnualUpgradeButton || showUndoCancellationCTA;
  const s12nName = coursePageData?.primarySpecialization?.name ?? s12nPageData?.name;
  const partnerIds = coursePageData?.partners?.map(({ id }) => id) ?? s12nPageData?.partners?.map(({ id }) => id);
  const sourceParam = query?.source;

  if (['teams', 'business'].includes(sourceParam)) {
    const config =
      sourceParam === 'teams'
        ? {
            href: '/purchase/plan',
            label: _t('Buy now'),
            trackingName: 'ent_website_search_flow_xdp_buy_now_cta' as const,
          }
        : {
            href: '/business/learn-more',
            label: _t('Contact sales'),
            trackingName: 'ent_website_search_flow_xdp_contact_sales_cta' as const,
          };

    return (
      <div css={styles.enrollButtonContainer()}>
        <TrackedCdsButton
          trackingName={config.trackingName}
          onClick={() => handleEnterpriseButtonClick(config.href, config.trackingName)}
        >
          {config.label}
        </TrackedCdsButton>
      </div>
    );
  }

  return (
    <div css={styles.enrollButtonContainer(showAdditionalCTA)}>
      {/* Using form element as a fallback if a user clicks on the button before TTI. After TTI onSubmit prevents default action and modal opens. */}
      <form
        onSubmit={onSubmit}
        action={isEnrolled && !s12nPageData?.id ? `${pathname}/home/welcome` : pathname}
        method="GET"
        css={styles.buttonContainer}
      >
        {!isEnrolled && <input type="hidden" name="action" value="enroll" />}
        <TrackedCdsButton
          _refAltAsTrueRef={!onStickyHeader ? enrollButtonRef : undefined}
          trackingName={buttonTrackingName || 'enroll_button'}
          disabled={buttonDisabled || buttonLoading}
          loading={buttonLoading}
          data-e2e="enroll-button"
          type="submit"
          onClick={handleEnrollButtonClick}
          size={onStickyHeader ? 'small' : 'medium'}
          css={shouldShowBiggerCTA ? styles.biggerButton : styles.button}
        >
          {buttonLabel}
        </TrackedCdsButton>
      </form>
      {!onStickyHeader && (
        <CourseraPlusMonthlyToAnnualUpgradeContainer
          isEligibleForPlusMonthlyToAnnualUpgrade={isEligibleForPlusMonthlyToAnnualUpgrade}
        />
      )}
      {!onStickyHeader && (
        <ReactivateSubscriptionXdpCtaButton
          isCourseraPlusEligibleProduct={isCourseraPlusEligibleProduct}
          productItemId={productItemId}
          s12nName={s12nName}
          partnerIds={partnerIds}
        />
      )}
      {!onStickyHeader && isS12nUpgradeEligible && (
        <TrackedCdsButton
          trackingName="view_updates"
          variant="secondary"
          size="medium"
          fullWidth
          css={styles.button}
          onClick={() => handleViewUpdatesButtonClick(s12nUpgradeLink)}
        >
          <div>{_t('View Latest Update')}</div>
        </TrackedCdsButton>
      )}
      {!onStickyHeader && (
        <div css={styles.additionalLinksContainer}>
          {title && <div css={styles.title}>{title}</div>}
          {TitleComponent && (
            <div css={styles.title}>
              <TitleComponent onMultipleLearningProgramsLinkClick={onMultipleLearningProgramsLinkClick} />
            </div>
          )}
          {subtitleElem && <div css={styles.subtitleContainer}>{subtitleElem}</div>}
        </div>
      )}
    </div>
  );
};

export default EnrollButtonContent;
